/*
 * Figma `126:157` — SA3-09 · Global Calendar.
 *
 * `TableTemplate`: every concert on the platform's public calendar, grouped by month, with an
 * import that reports what it did rather than claiming success.
 *
 * The frame asks for "event density, capacity risk, collisions, maintenance windows". Only the
 * first and third are backed: `ConcertCalendarDto` carries a date, a venue and a province, so
 * density per month and same-day collisions are real and derivable. Capacity risk and maintenance
 * windows have no data behind them here and are not invented — noted in the resume record.
 *
 * The import result is the part worth having. A spreadsheet of a hundred concerts will always have
 * rows that are duplicates or missing a date; an import that says "ok" and silently drops them is
 * how a calendar quietly loses a third of its entries. So every bucket is reported, including the
 * ones that mean "nothing happened".
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, FormDialog, KpiCard, SearchField, StatusBadge,
  TableTemplate, TextField,
} from '../..';
import type { Column } from '../..';
import type { Destination } from '../../destinations';
import type { ConcertCalendarDto, ConcertCalendarImportResultDto, ConcertImportDraftDto } from '../../../api';
import { useGlobalCalendar } from './useGlobalCalendar';

export interface GlobalCalendarState {
  entries: ConcertCalendarDto[];
  loading: boolean;
  loadError: string;
  error: string;
  busy: boolean;
  importResult: ConcertCalendarImportResultDto | null;
  importFile: (file: File) => Promise<void>;
  /**
   * TICK-051/TICK-142 — a draft scraped from one ticketing page, for review before it is saved.
   *
   * The endpoint reads an allow-listed page and never writes: what comes back is a *suggestion*,
   * best-effort, with fields the page may simply not have carried. `true` back means a draft
   * arrived and the review form is worth opening.
   */
  importDraft: ConcertImportDraftDto | null;
  importFromUrl: (url: string) => Promise<boolean>;
  clearDraft: () => void;
  /**
   * One show, typed by hand. The file import covers a spreadsheet of a hundred; this covers the
   * one somebody hears about on a Tuesday, which the rebuilt screen could not record at all.
   */
  create: (entry: Omit<ConcertCalendarDto, 'id' | 'createdAt'>) => Promise<boolean>;
  remove: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

const BLANK_ENTRY = {
  showDate: '', dateLabel: '', eventName: '', artist: '', venue: '', province: 'กรุงเทพมหานคร',
};

/*
 * `monthKey` and `sortDate` are derived here rather than typed.
 *
 * The classic form asked an operator for both by hand, defaulted to `2026-08` and `2026-08-01`,
 * and had no way to notice when the two disagreed with each other or with the date label. They are
 * a month and a day taken from one date, so one date is what the form asks for.
 */
const keysOf = (showDate: string) => ({ monthKey: showDate.slice(0, 7), sortDate: showDate });

/** Which fields of a review form the page actually supplied, and which a person still has to. */
export interface DraftProvenance {
  eventName: boolean;
  venue: boolean;
  /** `parsed` — a real date came back · `text` — something unparseable · `missing` — nothing at all. */
  date: 'parsed' | 'text' | 'missing';
  /** Always false. The extractor has no artist rule at all and returns an empty string every time. */
  artist: false;
  /** Always false. Nothing on a ticketing page is read as a province. */
  province: false;
}

/**
 * Turns a scraped draft into the fields the review form edits, and says where each one came from.
 *
 * Two things this has to get right, and both are why the draft cannot simply be poured into the
 * form:
 *
 * - **`dateLabel` from the page is not a display label.** The extractor reads JSON-LD `startDate`,
 *   which is an ISO timestamp — saving it as the label would put `2026-11-21T19:00:00+07:00` in a
 *   calendar column meant to read "21 พ.ย. 2569". A value that parses becomes the *date*, and the
 *   label is left for a person to write. One that does not parse is kept as the label, because then
 *   it is prose and prose is what the label is for.
 * - **A pre-filled form and a typed form look identical.** That is how a guess gets saved as a
 *   fact, so every field says which of the two it is.
 */
export function draftToForm(draft: ConcertImportDraftDto): {
  entry: typeof BLANK_ENTRY;
  provenance: DraftProvenance;
} {
  const raw = draft.dateLabel.trim();
  const parsed = raw ? new Date(raw) : null;
  const usable = parsed && !Number.isNaN(parsed.getTime());
  // TICK-174 — the day is the one the page wrote. Read through UTC, `2026-11-21T00:00:00+07:00`
  // became the 20th. An ISO value carries its own calendar day; anything else Date accepts was read
  // as browser-local time, so its local day is the one it names.
  const isoDay = raw.match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/)?.[1];
  const showDate = !usable ? ''
    : isoDay ?? `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;

  return {
    entry: {
      showDate,
      dateLabel: usable ? '' : raw,
      eventName: draft.eventName.trim(),
      artist: '',
      venue: draft.venue.trim(),
      province: BLANK_ENTRY.province,
    },
    provenance: {
      eventName: draft.eventName.trim() !== '',
      venue: draft.venue.trim() !== '',
      date: usable ? 'parsed' : raw ? 'text' : 'missing',
      artist: false,
      province: false,
    },
  };
}

/** Same venue, same day, twice. Real from the data, and the only collision it can prove. */
export function collisionsOf(entries: readonly ConcertCalendarDto[]): ConcertCalendarDto[][] {
  const byKey = new Map<string, ConcertCalendarDto[]>();
  for (const e of entries) {
    const key = `${e.sortDate}|${e.venue.trim().toLowerCase()}`;
    byKey.set(key, [...(byKey.get(key) ?? []), e]);
  }
  return [...byKey.values()].filter((group) => group.length > 1);
}

export function GlobalCalendarScreen({ destination, state }: {
  destination: Destination;
  state: GlobalCalendarState;
}) {
  const {
    entries, loading, loadError, error, busy, importResult, importFile, create, remove, reload,
    importDraft, importFromUrl, clearDraft,
  } = state;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(BLANK_ENTRY);
  const [url, setUrl] = useState('');
  /*
   * Held beside the form rather than recomputed from `importDraft`, so that editing a field does
   * not change what the form says about where that field came from. The provenance describes the
   * fetch, not the current values.
   */
  const [provenance, setProvenance] = useState<DraftProvenance | null>(null);

  /*
   * A draft arriving is what opens the review form. The fetch is one step and the review is the
   * next, and nothing is saved in between — so the screen does not ask again whether to show it.
   */
  useEffect(() => {
    if (!importDraft) return;
    const { entry, provenance: from } = draftToForm(importDraft);
    setDraft(entry);
    setProvenance(from);
    setAdding(true);
  }, [importDraft]);
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<ConcertCalendarDto | null>(null);

  const rows = entries.filter((e) => {
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return `${e.eventName} ${e.artist} ${e.venue} ${e.province}`.toLowerCase().includes(needle);
  });

  const months = new Set(entries.map((e) => e.monthKey));
  const collisions = collisionsOf(entries);
  const colliding = new Set(collisions.flat().map((e) => e.id));

  const columns: Column<ConcertCalendarDto>[] = [
    { key: 'date', header: 'วันที่', width: '160px', cell: (e) => e.dateLabel },
    {
      key: 'event',
      header: 'งาน',
      cell: (e) => (
        <span className="jt-cell-stack">
          <span>{e.eventName}</span>
          <span className="jt-cell-sub">{e.artist}</span>
        </span>
      ),
    },
    {
      key: 'venue',
      header: 'สถานที่',
      cell: (e) => (
        <span className="jt-cell-stack">
          <span>{e.venue}</span>
          <span className="jt-cell-sub">{e.province}</span>
        </span>
      ),
    },
    {
      key: 'flags',
      header: '',
      width: '150px',
      cell: (e) => (colliding.has(e.id) ? <StatusBadge tone="warning">ชนวันเดียวกัน</StatusBadge> : null),
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      align: 'end',
      cell: (e) => <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmDelete(e)}>ลบ</Button>,
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload }}
      kpis={[
        <KpiCard key="all" label="งานในปฏิทิน" value={entries.length.toLocaleString('th-TH')} />,
        <KpiCard key="months" label="เดือนที่มีงาน" value={months.size.toLocaleString('th-TH')} />,
        <KpiCard
          key="clash"
          label="ชนวันและสถานที่เดียวกัน"
          value={collisions.length.toLocaleString('th-TH')}
          tone={collisions.length > 0 ? 'warning' : undefined}
        />,
      ]}
      summary={
        <>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
          {collisions.length > 0 && (
            <Alert tone="warning" title={`พบ ${collisions.length} กลุ่มที่ชนกัน`}>
              {collisions.slice(0, 3).map((group) => `${group[0].dateLabel} · ${group[0].venue}`).join(' · ')}
              {collisions.length > 3 && ' และอื่น ๆ'} — อาจเป็นข้อมูลซ้ำจากการนำเข้า หรือเป็นสองงานคนละรอบจริง ๆ
            </Alert>
          )}
          <Card title="เพิ่มงานทีละรายการ" sub="สำหรับงานที่รู้มาเป็นงาน ๆ — ไฟล์ด้านล่างไว้สำหรับทั้งตาราง">
            <div className="jt-inline-actions">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => { setDraft(BLANK_ENTRY); setProvenance(null); clearDraft(); setAdding(true); }}
              >
                ＋ เพิ่มงาน
              </Button>
            </div>
          </Card>

          {/*
            * The third way in, beside one-at-a-time and a whole spreadsheet: a link to one show on
            * a ticketing site. The endpoint reads the page and never writes — what comes back is a
            * suggestion that a person confirms, which is why this opens the same form rather than
            * saving anything.
            */}
          <Card title="ดึงงานจากลิงก์" sub="วางลิงก์หน้างานจากเว็บขายบัตร แล้วตรวจก่อนบันทึก">
            <TextField
              label="ลิงก์หน้างาน"
              type="url"
              value={url}
              placeholder="https://…"
              onChange={(e) => setUrl(e.target.value)}
              hint="รองรับเฉพาะเว็บขายบัตรที่อนุญาตไว้ — ถ้าไม่รองรับ ระบบจะบอกว่ามีเว็บไหนบ้าง"
            />
            <div className="jt-inline-actions">
              <Button
                size="sm"
                variant="secondary"
                disabled={busy || !url.trim()}
                onClick={async () => {
                  if (await importFromUrl(url.trim())) setUrl('');
                }}
              >
                {busy ? 'กำลังดึง…' : 'ดึงข้อมูล'}
              </Button>
              <span className="jt-field-hint">ยังไม่บันทึกอะไรจนกว่าจะกดยืนยันในฟอร์ม</span>
            </div>
          </Card>

          <Card title="นำเข้าจากไฟล์" sub="ไฟล์รายชื่อคอนเสิร์ตจากแหล่งภายนอก">
            <div className="jt-inline-actions">
              <label className="jt-btn" data-style="secondary" data-size="sm" data-emphasis="normal">
                เลือกไฟล์
                <input
                  type="file"
                  className="jt-sr-only"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void importFile(file);
                  }}
                />
              </label>
              {busy && <span className="jt-field-hint">กำลังนำเข้า…</span>}
            </div>
            {importResult && (
              <Alert
                tone={importResult.summary.imported > 0 ? 'success' : 'warning'}
                title={`นำเข้า ${importResult.summary.imported} จาก ${importResult.summary.total} แถว`}
              >
                <ul className="jt-consequences">
                  <li>ซ้ำกับที่มีอยู่แล้ว {importResult.summary.duplicate} แถว — ข้ามไป ไม่ได้เขียนทับ</li>
                  <li>ไม่มีวันที่ {importResult.summary.missingDate} แถว — ไม่นำเข้า เพราะจัดลงปฏิทินไม่ได้</li>
                  <li>รูปแบบไม่ถูกต้อง {importResult.summary.invalid} แถว</li>
                </ul>
              </Alert>
            )}
          </Card>
        </>
      }
      toolbar={<SearchField value={query} onChange={setQuery} label="ค้นหางาน" placeholder="ชื่องาน / ศิลปิน / สถานที่" />}
      table={{
        label: 'ปฏิทินคอนเสิร์ต',
        columns,
        rows,
        rowKey: (e) => e.id,
        filtered: query.trim().length > 0,
        onClearFilters: () => setQuery(''),
        emptyTitle: 'ยังไม่มีงานในปฏิทิน',
        emptyText: 'นำเข้าจากไฟล์ด้านบนเพื่อเริ่มต้น',
        card: (e) => (
          <span className="jt-cell-stack">
            <span>{e.eventName}</span>
            <span className="jt-cell-sub">{e.dateLabel} · {e.venue}</span>
          </span>
        ),
      }}
      drawer={
        <>
        <FormDialog
          open={adding}
          onClose={() => { setAdding(false); setProvenance(null); clearDraft(); }}
          onSubmit={() => {
            const { monthKey, sortDate } = keysOf(draft.showDate);
            void create({
              dateLabel: draft.dateLabel.trim() || draft.showDate,
              eventName: draft.eventName.trim(),
              artist: draft.artist.trim(),
              venue: draft.venue.trim(),
              province: draft.province.trim(),
              monthKey,
              sortDate,
            }).then((ok) => { if (ok) { setAdding(false); setProvenance(null); clearDraft(); } });
          }}
          title={provenance ? 'ตรวจร่างก่อนบันทึก' : 'เพิ่มงานเข้าปฏิทิน'}
          description="ข้อมูลภายในสำหรับเลี่ยงวันชนกัน — ไม่เปิดสาธารณะ"
          submitLabel={provenance ? 'บันทึกเข้าปฏิทิน' : 'เพิ่มเข้าปฏิทิน'}
          busy={busy}
          error={error || null}
        >
          {/*
            * Said once at the top, then again per field. The banner is what stops a scraped form
            * being read as a typed one; the per-field notes are what say which half is which.
            */}
          {provenance && (
            <>
              <Alert tone="info" title="นี่คือร่างจากหน้าเว็บ ยังไม่ได้บันทึก">
                ดึงมาจาก <span className="jt-mono">{importDraft?.sourceUrl}</span> — ตรวจให้ครบก่อนกดบันทึก
              </Alert>
              {(!provenance.eventName || provenance.date === 'missing') && (
                <Alert tone="warning" title="หน้าเว็บไม่ได้ให้ข้อมูลบางอย่าง">
                  {[
                    !provenance.eventName ? 'ชื่องาน' : null,
                    provenance.date === 'missing' ? 'วันที่' : null,
                  ].filter(Boolean).join(' และ ')} ต้องกรอกเอง
                </Alert>
              )}
            </>
          )}
          <TextField
            label="วันที่จัด"
            type="date"
            value={draft.showDate}
            onChange={(e) => setDraft({ ...draft, showDate: e.target.value })}
            hint={provenance
              ? provenance.date === 'parsed'
                ? 'อ่านได้จากหน้าเว็บ — ตรวจว่าตรงกับรอบที่ต้องการ'
                : 'หน้าเว็บไม่ได้ให้วันที่ที่อ่านเป็นวันได้ — กรอกเอง'
              : 'ใช้จัดเรียงและจัดกลุ่มตามเดือนให้เอง'}
          />
          <TextField
            label="วันที่แสดงผล"
            value={draft.dateLabel}
            onChange={(e) => setDraft({ ...draft, dateLabel: e.target.value })}
            hint={provenance?.date === 'text'
              ? 'ข้อความวันที่จากหน้าเว็บ อ่านเป็นวันไม่ได้ — แก้ให้อ่านง่ายได้'
              : 'เว้นว่างได้ — ใส่เองเมื่อเป็นช่วงวัน เช่น 21–22 พ.ย. 2569'}
          />
          <TextField
            label="ชื่องาน"
            value={draft.eventName}
            onChange={(e) => setDraft({ ...draft, eventName: e.target.value })}
            hint={provenance
              ? provenance.eventName ? 'ดึงมาจากหน้าเว็บ' : 'หน้าเว็บไม่ได้ให้มา — กรอกเอง'
              : undefined}
          />
          <TextField
            label="ศิลปิน"
            value={draft.artist}
            onChange={(e) => setDraft({ ...draft, artist: e.target.value })}
            /* Never scraped: the extractor has no artist rule and returns '' every time. */
            hint={provenance ? 'การดึงจากลิงก์ไม่อ่านชื่อศิลปิน — กรอกเองเสมอ' : undefined}
          />
          <TextField
            label="สถานที่"
            value={draft.venue}
            onChange={(e) => setDraft({ ...draft, venue: e.target.value })}
            hint={provenance
              ? provenance.venue
                ? 'ดึงมาจากหน้าเว็บ — สะกดให้เหมือนรายการเดิมถ้าเป็นที่เดียวกัน'
                : 'หน้าเว็บไม่ได้ให้มา — กรอกเอง'
              : 'ชนกันได้ก็ต่อเมื่อสถานที่และวันตรงกัน — สะกดให้เหมือนเดิมถ้าเป็นที่เดิม'}
          />
          <TextField
            label="จังหวัด"
            value={draft.province}
            onChange={(e) => setDraft({ ...draft, province: e.target.value })}
            hint={provenance ? 'การดึงจากลิงก์ไม่อ่านจังหวัด — ค่าตั้งต้นคือกรุงเทพฯ' : undefined}
          />
        </FormDialog>

        <ConfirmDialog
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            const target = confirmDelete;
            setConfirmDelete(null);
            if (target) void remove(target.id);
          }}
          title={`ลบ ${confirmDelete?.eventName ?? ''} ออกจากปฏิทิน`}
          confirmLabel="ลบรายการนี้"
          consequences={[
            'รายการจะหายจากปฏิทินสาธารณะบนหน้าแรกทันที',
            'ไม่กระทบอีเวนต์ที่ขายบัตรอยู่บน JamTick — ปฏิทินนี้เป็นรายการอ้างอิงคนละชุดกัน',
            'ถ้านำเข้าไฟล์เดิมอีกครั้ง รายการนี้จะกลับมา',
          ]}
        />
        </>
      }
    />
  );
}

export function GlobalCalendarDestination({ token, destination }: { token: string; destination: Destination }) {
  const state = useGlobalCalendar(token);
  return <GlobalCalendarScreen destination={destination} state={state} />;
}
